/**
 * usePipelineEvents Hook
 *
 * React hook for subscribing to pipeline events via Server-Sent Events (SSE).
 * Provides real-time updates with automatic reconnection for processing pipeline status.
 * Replaces polling-based status updates with push-based SSE.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import type {
  PipelineStatus,
  PipelineEvent,
  PipelineStage,
  PipelineError,
} from "@/types/pipeline";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8101";
const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Options for the usePipelineEvents hook
 */
export interface UsePipelineEventsOptions {
  /** Reflection ID to monitor */
  reflectionId: string;
  /** Callback when stage changes */
  onStageChange?: (stage: PipelineStage) => void;
  /** Callback when progress updates */
  onProgress?: (progress: number) => void;
  /** Callback when pipeline completes successfully */
  onComplete?: () => void;
  /** Callback when an error occurs */
  onError?: (error: PipelineError) => void;
  /** Whether to automatically connect (default true) */
  enabled?: boolean;
  /** Access token for authentication */
  getAccessToken?: () => Promise<string>;
}

/**
 * Return type for usePipelineEvents hook
 */
export interface UsePipelineEventsResult {
  /** Current pipeline status */
  status: PipelineStatus | null;
  /** Whether SSE connection is active */
  isConnected: boolean;
  /** Error message if connection failed */
  error: string | null;
  /** Whether the pipeline is currently processing */
  isProcessing: boolean;
  /** Whether the pipeline completed successfully */
  isComplete: boolean;
  /** Whether the pipeline failed */
  isFailed: boolean;
  /** Overall progress percentage (0-100) */
  progress: number;
  /** Manually reconnect to SSE stream */
  reconnect: () => void;
}

/**
 * Hook for subscribing to pipeline events via SSE
 *
 * Establishes a persistent SSE connection to receive real-time pipeline updates.
 * Automatically handles reconnection on network errors and provides callbacks
 * for key pipeline events.
 *
 * @param options - Configuration options
 * @returns Pipeline status and connection state
 *
 * @example
 * ```tsx
 * const { status, isConnected, progress } = usePipelineEvents({
 *   reflectionId: reflection.id,
 *   onComplete: () => router.push('/reflection/complete'),
 *   onError: (err) => toast.error(err.message),
 * });
 * ```
 */
export function usePipelineEvents(
  options: UsePipelineEventsOptions
): UsePipelineEventsResult {
  const {
    reflectionId,
    onStageChange,
    onProgress,
    onComplete,
    onError,
    enabled = true,
    getAccessToken,
  } = options;

  const [status, setStatus] = useState<PipelineStatus | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use refs for callbacks to avoid recreating effects
  const onStageChangeRef = useRef(onStageChange);
  const onProgressRef = useRef(onProgress);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const getAccessTokenRef = useRef(getAccessToken);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Update refs when callbacks change
  useEffect(() => {
    onStageChangeRef.current = onStageChange;
    onProgressRef.current = onProgress;
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
    getAccessTokenRef.current = getAccessToken;
  }, [onStageChange, onProgress, onComplete, onError, getAccessToken]);

  /**
   * Parse SSE message data
   */
  const parseEventData = useCallback((data: string): unknown => {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }, []);

  /**
   * Handle pipeline event
   */
  const handleEvent = useCallback((eventType: string, data: unknown) => {
    // Ignore heartbeat events - they're just for keeping the connection alive
    if (eventType === "heartbeat") {
      return;
    }

    // Handle pipeline state updates (full status snapshot)
    if (eventType === "pipeline.state") {
      const newStatus = data as PipelineStatus;
      setStatus(newStatus);
      onProgressRef.current?.(newStatus.overall_progress);
      return;
    }

    // Handle generic pipeline events
    const event = data as PipelineEvent;

    // Update status with event data
    setStatus((prevStatus) => {
      if (!prevStatus) {
        return null;
      }

      const updatedStatus = { ...prevStatus };

      // Only update stage/progress if event has valid data
      if (event.stage) {
        updatedStatus.current_stage = event.stage;
      }
      if (typeof event.overall_progress === "number") {
        updatedStatus.overall_progress = event.overall_progress;
      }

      // Update per-stage progress if available
      if (
        event.stage &&
        typeof event.progress === "number" &&
        updatedStatus.stages?.[event.stage]
      ) {
        updatedStatus.stages = {
          ...updatedStatus.stages,
          [event.stage]: {
            ...updatedStatus.stages[event.stage],
            progress: event.progress,
          },
        };
      }

      // Mark as completed when pipeline.completed event is received
      if (eventType === "pipeline.completed") {
        updatedStatus.current_status = "completed";
        // Also mark all stages as completed to ensure UI updates properly
        if (updatedStatus.stages) {
          const completedStages = { ...updatedStatus.stages };
          for (const stageName of Object.keys(completedStages)) {
            const stage =
              completedStages[stageName as keyof typeof completedStages];
            if (
              stage &&
              stage.status !== "skipped" &&
              stage.status !== "failed"
            ) {
              completedStages[stageName as keyof typeof completedStages] = {
                ...stage,
                status: "completed",
                progress: 100,
              };
            }
          }
          updatedStatus.stages = completedStages;
        }
      }

      // Mark as failed when stage.failed or pipeline.failed event is received
      if (eventType === "stage.failed" || eventType === "pipeline.failed") {
        updatedStatus.current_status = "failed";
        if (event.error) {
          updatedStatus.error = event.error;
        }
      }

      return updatedStatus;
    });

    // Trigger callbacks based on event type
    if (eventType === "stage.started" || eventType === "stage.progress") {
      onStageChangeRef.current?.(event.stage);
      onProgressRef.current?.(event.overall_progress);
    }

    if (eventType === "pipeline.completed") {
      onCompleteRef.current?.();
    }

    if (eventType === "stage.failed" || eventType === "pipeline.failed") {
      if (event.error) {
        onErrorRef.current?.(event.error);
        setError(event.error.message);
      }
    }
  }, []);

  /**
   * Connect to SSE stream
   */
  const connectSSE = useCallback(async () => {
    if (!enabled || !reflectionId || !isMountedRef.current) return;

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Abort previous connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Get access token if available
      let token = "";
      if (getAccessTokenRef.current) {
        try {
          token = await getAccessTokenRef.current();
        } catch (err) {
          if (!isMountedRef.current) return;
          console.error("Failed to get access token:", err);
          setError("Authentication failed");
          return;
        }
      }

      // Check if still mounted after async operation
      if (!isMountedRef.current) return;

      // Construct SSE URL
      const url = `${API_BASE_URL}${API_PREFIX}/pipeline/events/${reflectionId}`;

      // Fetch with SSE headers
      const response = await fetch(url, {
        headers: {
          Accept: "text/event-stream",
          Authorization: token ? `Bearer ${token}` : "",
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      if (!isMountedRef.current) return;

      setIsConnected(true);
      setError(null);

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // Track current event and data across chunks (SSE messages can span multiple chunks)
      let currentEvent = "message";
      let currentData = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();

        if (done || !isMountedRef.current) {
          if (isMountedRef.current) setIsConnected(false);
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Normalize line endings (handle \r\n Windows-style and lone \r)
        buffer = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

        // Process complete messages
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          // Trim to handle any remaining whitespace
          const trimmedLine = line.trim();

          if (trimmedLine.startsWith("event:")) {
            currentEvent = trimmedLine.slice(6).trim();
          } else if (trimmedLine.startsWith("data:")) {
            currentData = trimmedLine.slice(5).trim();
          } else if (trimmedLine === "") {
            // Empty line indicates end of message
            if (currentData && isMountedRef.current) {
              const parsedData = parseEventData(currentData);
              handleEvent(currentEvent, parsedData);
              // Reset for next event
              currentEvent = "message";
              currentData = "";
            }
          }
        }
      }
    } catch (err) {
      // Handle abort errors silently (happens during cleanup)
      if ((err as Error).name === "AbortError") {
        return;
      }

      // Handle network errors that occur during abort (shows as TypeError)
      if (
        err instanceof TypeError &&
        (err.message.includes("network") || err.message.includes("abort"))
      ) {
        return;
      }

      // Don't update state if unmounted
      if (!isMountedRef.current) return;

      console.error("SSE connection error:", err);
      setError(err instanceof Error ? err.message : "Connection failed");
      setIsConnected(false);

      // Attempt to reconnect after 3 seconds if still mounted
      if (enabled && isMountedRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connectSSE();
          }
        }, 3000);
      }
    }
  }, [enabled, reflectionId, parseEventData, handleEvent]);

  /**
   * Manually reconnect to SSE stream
   */
  const reconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    connectSSE();
  }, [connectSSE]);

  // Connect on mount and when dependencies change
  useEffect(() => {
    isMountedRef.current = true;
    connectSSE();

    return () => {
      // Mark as unmounted first to prevent state updates
      isMountedRef.current = false;

      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Cleanup: abort connection on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [connectSSE]);

  return {
    status,
    isConnected,
    error,
    isProcessing:
      status?.current_status === "in_progress" ||
      status?.current_status === "pending",
    isComplete: status?.current_status === "completed",
    isFailed: status?.current_status === "failed",
    progress: status?.overall_progress ?? 0,
    reconnect,
  };
}
